import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button, Input, Alert, Select, Badge } from '@/components/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, CardExpandable } from '@/components/ui/Card/Card';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete';
import { VenueMap } from '@/components/ui/VenueMap';
import { VenuesService, type Venue, type EnrichmentData, type EnrichmentHistoryEntry, type TripAdvisorReview } from '@/services/VenuesService';
import { VenueComments } from '@/components/venues/VenueComments';
import { VenueImagesPanel } from '@/components/venues/VenueImagesPanel';
import { useNotificationStore } from '@/stores/notificationStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Building2, MapPin, Hotel, Plane, Info, Brain, RotateCcw, Clock, CheckCircle, XCircle, ChevronDown, Utensils, Wine, Waves, Sparkles, Palmtree, UtensilsCrossed, Users, Flag, Image as ImageIcon } from 'lucide-react';
import { StarRating } from '@/components/ui/StarRating';

interface Props {
  isOpen: boolean;
  venue: Venue | null;
  onClose: () => void;
  onSave: () => void;
}

export function VenueFormModal({ isOpen, venue, onClose, onSave }: Props) {
  const [formData, setFormData] = useState<any>({
    property_name: '',
    region: '',
    country: '',
    state: '',
    city: '',
    address: '',
    place_id: null,
    website: '',
    number_of_rooms: '',
    meeting_spaces: '',
    maximum_capacity: '',
    star_rating: '',
    travel_time_minutes: '',
    // Amenities
    number_of_restaurants: '',
    number_of_bars: '',
    number_of_pools: '',
    spa_facilities: null,
    beach_access: '',
    all_inclusive_available: null,
    property_type: '',
    golf_course: '',
    // TripAdvisor
    tripadvisor_link: '',
    tripadvisor_rating: '',
    tripadvisor_review_count: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [isLocationDisabled, setIsLocationDisabled] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [expandedHistoryEntries, setExpandedHistoryEntries] = useState<Set<number>>(new Set([0])); // Most recent (index 0) expanded by default
  const { push: addNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const venuesService = new VenuesService();

  const resetMutation = useMutation({
    mutationFn: (venueId: string) => venuesService.resetEnrichment(venueId),
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Research Reset',
        message: 'Research status has been reset successfully'
      });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      onSave(); // Refresh parent list
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Reset Failed',
        message: error instanceof Error ? error.message : 'Failed to reset research'
      });
    }
  });

  const toggleHistoryEntry = (idx: number) => {
    setExpandedHistoryEntries(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  useEffect(() => {
    if (venue) {
      setFormData({
        property_name: venue.property_name || '',
        region: venue.region || '',
        country: venue.country || '',
        state: venue.state || '',
        city: venue.city || '',
        address: venue.address || '',
        place_id: venue.place_id || null,
        website: venue.website || '',
        number_of_rooms: venue.number_of_rooms?.toString() || '',
        meeting_spaces: venue.meeting_spaces?.toString() || '',
        maximum_capacity: venue.maximum_capacity?.toString() || '',
        star_rating: venue.star_rating?.toString() || '',
        travel_time_minutes: venue.travel_time_minutes?.toString() || '',
        // Amenities
        number_of_restaurants: venue.number_of_restaurants?.toString() || '',
        number_of_bars: venue.number_of_bars?.toString() || '',
        number_of_pools: venue.number_of_pools?.toString() || '',
        spa_facilities: venue.spa_facilities ?? null,
        beach_access: venue.beach_access || '',
        all_inclusive_available: venue.all_inclusive_available ?? null,
        property_type: venue.property_type || '',
        golf_course: venue.golf_course || '',
        // TripAdvisor
        tripadvisor_link: venue.tripadvisor_link || '',
        tripadvisor_rating: venue.tripadvisor_rating?.toString() || '',
        tripadvisor_review_count: venue.tripadvisor_review_count?.toString() || ''
      });
      // If venue already has location data, disable location fields
      if (venue.city || venue.state || venue.country) {
        setIsLocationDisabled(true);
      }
    } else {
      setFormData({
        property_name: '',
        region: '',
        country: '',
        state: '',
        city: '',
        address: '',
        place_id: null,
        website: '',
        number_of_rooms: '',
        meeting_spaces: '',
        maximum_capacity: '',
        star_rating: '',
        travel_time_minutes: '',
        // Amenities
        number_of_restaurants: '',
        number_of_bars: '',
        number_of_pools: '',
        spa_facilities: null,
        beach_access: '',
        all_inclusive_available: null,
        property_type: '',
        golf_course: '',
        // TripAdvisor
        tripadvisor_link: '',
        tripadvisor_rating: '',
        tripadvisor_review_count: ''
      });
      setLocationInput('');
      setIsLocationDisabled(false);
    }
    setActiveTab('basic'); // Reset to first tab when modal opens
  }, [venue, isOpen]);

  async function handleLocationSelect(locationData: LocationData) {
    // Store place_id from the selection
    const placeId = locationData.place_id;
    
    // Fetch Place Details to get full address information
    // This is especially important for establishments
    try {
      const detailsResponse = await apiFetch(`/api/places/details?place_id=${encodeURIComponent(placeId)}&fields=geometry,address_components,formatted_address,types,name`);
      if (detailsResponse.ok) {
        const detailsData = await detailsResponse.json();
        const placeDetails = detailsData.data;
        
        // Determine if this is an establishment (not just an address)
        const isEstablishment = placeDetails.types && 
          (placeDetails.types.includes('lodging') || 
           placeDetails.types.includes('establishment') ||
           placeDetails.types.includes('point_of_interest'));
        
        // Extract address components
        const addressComponents = placeDetails.address_components || [];
        let city = locationData.city;
        let state = locationData.region;
        let country = locationData.country;
        let streetNumber = '';
        let route = '';
        
        // Parse address components to extract street address separately from city/state/country
        for (const component of addressComponents) {
          const types = component.types as string[];
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            route = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            state = component.long_name;
          } else if (types.includes('country')) {
            country = component.long_name;
          }
        }
        
        // Build street address (street number + route) without city/state/country
        // For establishments, this gives us the actual venue address
        // For addresses, this gives us just the street address
        let address = '';
        if (streetNumber && route) {
          address = `${streetNumber} ${route}`;
        } else if (route) {
          address = route;
        } else if (streetNumber) {
          address = streetNumber;
        } else {
          // Fallback: use formatted_address but try to remove city/state/country
          // Split by comma and take first part (usually the street address)
          const formattedAddr = placeDetails.formatted_address || locationData.formatted_address;
          if (formattedAddr) {
            const parts = formattedAddr.split(',');
            // Take first part if it looks like a street address (has numbers or street keywords)
            if (parts[0] && (/\d/.test(parts[0]) || 
                parts[0].includes('St') || 
                parts[0].includes('Ave') || 
                parts[0].includes('Rd') || 
                parts[0].includes('Blvd') ||
                parts[0].includes('Street') ||
                parts[0].includes('Avenue') ||
                parts[0].includes('Road'))) {
              address = parts[0].trim();
            } else {
              // If first part doesn't look like address, use it anyway as fallback
              address = parts[0]?.trim() || formattedAddr;
            }
          }
        }
        
        // Auto-populate location fields from Google Places
        setFormData((f: typeof formData) => ({
          ...f,
          city: city || '',
          state: state || '',
          country: country || '',
          address: address || f.address,
          place_id: placeId // Store the place_id
        }));
        
        // Disable individual location fields since they're auto-populated
        setIsLocationDisabled(true);
        
        const message = isEstablishment
          ? 'Establishment and location auto-populated from Google Places'
          : 'Address and location auto-populated from Google Places';
        addNotification({
          type: 'success',
          title: 'Location Updated',
          message
        });
      } else {
        // Fallback to basic location data if Place Details fails
        // Try to extract street address from formatted_address
        let address = locationData.formatted_address || '';
        if (address) {
          const parts = address.split(',');
          // Take first part if it looks like a street address
          if (parts[0] && (/\d/.test(parts[0]) || 
              parts[0].includes('St') || 
              parts[0].includes('Ave') || 
              parts[0].includes('Rd') || 
              parts[0].includes('Blvd') ||
              parts[0].includes('Street') ||
              parts[0].includes('Avenue') ||
              parts[0].includes('Road'))) {
            address = parts[0].trim();
          }
        }
        
        setFormData((f: typeof formData) => ({
          ...f,
          city: locationData.city || '',
          state: locationData.region || '',
          country: locationData.country || '',
          address: address || f.address,
          place_id: placeId
        }));
        setIsLocationDisabled(true);
        addNotification({
          type: 'success',
          title: 'Location Updated',
          message: 'Location auto-populated from Google Places'
        });
      }
    } catch (error) {
      console.error('Failed to fetch place details:', error);
      // Fallback to basic location data
      // Try to extract street address from formatted_address
      let address = locationData.formatted_address || '';
      if (address) {
        const parts = address.split(',');
        // Take first part if it looks like a street address
        if (parts[0] && (/\d/.test(parts[0]) || 
            parts[0].includes('St') || 
            parts[0].includes('Ave') || 
            parts[0].includes('Rd') || 
            parts[0].includes('Blvd') ||
            parts[0].includes('Street') ||
            parts[0].includes('Avenue') ||
            parts[0].includes('Road'))) {
          address = parts[0].trim();
        }
      }
      
      setFormData((f: typeof formData) => ({
        ...f,
        city: locationData.city || '',
        state: locationData.region || '',
        country: locationData.country || '',
        address: address || f.address,
        place_id: placeId
      }));
      setIsLocationDisabled(true);
      addNotification({
        type: 'success',
        title: 'Location Updated',
        message: 'Location auto-populated from Google Places'
      });
    }
  }

  function clearLocation() {
    setLocationInput('');
    setIsLocationDisabled(false);
    setFormData((f: typeof formData) => ({
      ...f,
      city: '',
      state: '',
      country: '',
      place_id: null
      // Keep address as user might want to keep it
    }));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.property_name.trim()) {
      addNotification({
        type: 'error',
        title: 'Validation Error',
        message: 'Property name is required'
      });
      return;
    }

    setIsSaving(true);
    try {
      const payload: any = {
        property_name: formData.property_name.trim(),
        region: formData.region.trim() || null,
        country: formData.country.trim() || null,
        state: formData.state.trim() || null,
        city: formData.city.trim() || null,
        address: formData.address.trim() || null,
        place_id: formData.place_id || null,
        website: formData.website.trim() || null,
        number_of_rooms: formData.number_of_rooms ? parseInt(formData.number_of_rooms) : null,
        meeting_spaces: formData.meeting_spaces ? parseInt(formData.meeting_spaces) : null,
        maximum_capacity: formData.maximum_capacity ? parseInt(formData.maximum_capacity) : null,
        star_rating: formData.star_rating ? parseInt(formData.star_rating) : null,
        travel_time_minutes: formData.travel_time_minutes ? parseInt(formData.travel_time_minutes) : null,
        // Amenities
        number_of_restaurants: formData.number_of_restaurants ? parseInt(formData.number_of_restaurants) : null,
        number_of_bars: formData.number_of_bars ? parseInt(formData.number_of_bars) : null,
        number_of_pools: formData.number_of_pools ? parseInt(formData.number_of_pools) : null,
        spa_facilities: formData.spa_facilities ?? null,
        beach_access: formData.beach_access || null,
        all_inclusive_available: formData.all_inclusive_available ?? null,
        property_type: formData.property_type || null,
        golf_course: formData.golf_course || null,
        // TripAdvisor
        tripadvisor_link: formData.tripadvisor_link.trim() || null,
        tripadvisor_rating: formData.tripadvisor_rating ? parseFloat(formData.tripadvisor_rating) : null,
        tripadvisor_review_count: formData.tripadvisor_review_count ? parseInt(formData.tripadvisor_review_count) : null
      };

      if (venue) {
        await venuesService.update(venue.id, payload);
        addNotification({
          type: 'success',
          title: 'Venue Updated',
          message: 'Venue has been successfully updated'
        });
      } else {
        await venuesService.create(payload);
        addNotification({
          type: 'success',
          title: 'Venue Created',
          message: 'Venue has been successfully created'
        });
      }

      onSave();
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: string, value: string | boolean | null) => {
    setFormData({ ...formData, [field]: value });
  };

  const hasGeolocation = venue?.lat && venue?.lon;
  const hasAirportData = venue?.nearest_airport_iata;
  
  // Parse enrichment data
  const enrichmentData: EnrichmentData | null = venue?.enrichment_data_json 
    ? (() => {
        try {
          // Handle both string and already-parsed object cases
          if (typeof venue.enrichment_data_json === 'string') {
            return JSON.parse(venue.enrichment_data_json);
          } else if (typeof venue.enrichment_data_json === 'object') {
            return venue.enrichment_data_json;
          } else {
            return null;
          }
        } catch {
          return null;
        }
      })()
    : null;
  
  // Parse enrichment history
  const enrichmentHistory: EnrichmentHistoryEntry[] = venue?.enrichment_history_json 
    ? (() => {
        try {
          // Handle both string and already-parsed object cases
          // Knex returns JSONB columns as parsed objects, not strings
          if (typeof venue.enrichment_history_json === 'string') {
            return JSON.parse(venue.enrichment_history_json);
          } else if (Array.isArray(venue.enrichment_history_json)) {
            return venue.enrichment_history_json;
          } else {
            console.warn('enrichment_history_json is neither string nor array:', venue.enrichment_history_json);
            return [];
          }
        } catch (e) {
          console.error('Failed to parse enrichment_history_json:', e);
          return [];
        }
      })()
    : [];
  
  const hasEnrichmentData = enrichmentHistory.length > 0 || (enrichmentData && enrichmentData.agent_notes && enrichmentData.agent_notes.length > 0);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={venue ? 'Edit Venue' : 'Add Venue'}
      size="xl"
    >
      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-7 mb-6">
            <TabsTrigger value="basic" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Basic Info</span>
            </TabsTrigger>
            <TabsTrigger value="location" className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span className="hidden sm:inline">Location</span>
            </TabsTrigger>
            <TabsTrigger value="details" className="flex items-center gap-2">
              <Hotel className="w-4 h-4" />
              <span className="hidden sm:inline">Details</span>
            </TabsTrigger>
            <TabsTrigger value="airports" className="flex items-center gap-2" disabled={!hasAirportData}>
              <Plane className="w-4 h-4" />
              <span className="hidden sm:inline">Airports</span>
            </TabsTrigger>
            <TabsTrigger value="ai-agents" className="flex items-center gap-2">
              <Brain className="w-4 h-4" />
              <span className="hidden sm:inline">AI Agents</span>
            </TabsTrigger>
            <TabsTrigger value="notes" className="flex items-center gap-2">
              <Info className="w-4 h-4" />
              <span className="hidden sm:inline">Notes</span>
            </TabsTrigger>
            <TabsTrigger value="images" className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Images</span>
            </TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.property_name}
                onChange={(e) => handleChange('property_name', e.target.value)}
                placeholder="e.g., Marriott Downtown"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <Input
                type="url"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Star Rating (1-5)</label>
              <Input
                type="number"
                min="1"
                max="5"
                value={formData.star_rating}
                onChange={(e) => handleChange('star_rating', e.target.value)}
                placeholder="1-5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Region (Continent)</label>
              <Input
                value={formData.region}
                onChange={(e) => handleChange('region', e.target.value)}
                placeholder="e.g., North America, Europe, Asia"
              />
            </div>

            {/* TripAdvisor Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-primary-600" />
                TripAdvisor Information
              </h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">TripAdvisor Link</label>
                <Input
                  type="url"
                  value={formData.tripadvisor_link}
                  onChange={(e) => handleChange('tripadvisor_link', e.target.value)}
                  placeholder="https://www.tripadvisor.com/..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">TripAdvisor Rating (out of 5.0)</label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={formData.tripadvisor_rating}
                    onChange={(e) => handleChange('tripadvisor_rating', e.target.value)}
                    placeholder="e.g., 4.5"
                  />
                  {formData.tripadvisor_rating && (
                    <div className="mt-1 flex items-center gap-2">
                      <StarRating rating={parseFloat(formData.tripadvisor_rating) || 0} size="sm" showEmptyStars={false} />
                      <span className="text-sm text-gray-600">({formData.tripadvisor_rating}/5.0)</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Review Count</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.tripadvisor_review_count}
                    onChange={(e) => handleChange('tripadvisor_review_count', e.target.value)}
                    placeholder="Number of reviews"
                  />
                </div>
              </div>

              <Alert variant="info" className="mt-4">
                AI enrichment can automatically populate TripAdvisor data including link, rating, and recent reviews.
              </Alert>
            </div>
          </TabsContent>

          {/* Location & Map Tab */}
          <TabsContent value="location" className="space-y-4">
            <Alert variant="info">
              Search for the venue address to auto-populate location fields and enable geocoding.
            </Alert>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search Location</label>
                          <LocationAutocomplete
                            value={locationInput}
                            onChange={setLocationInput}
                            onLocationSelect={handleLocationSelect}
                            placeholder="Search for venue name or address (e.g., The Westin Minneapolis or 123 Main St)"
                            types="establishment"
                          />
              {isLocationDisabled && (
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-green-600">✓ Location auto-populated from Google Places</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearLocation}
                  >
                    Clear & Edit Manually
                  </Button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="e.g., 123 Main Street"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <Input
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="e.g., New York"
                  disabled={isLocationDisabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State/Region</label>
                <Input
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="e.g., NY, California"
                  disabled={isLocationDisabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <Input
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  placeholder="e.g., United States"
                  disabled={isLocationDisabled}
                />
              </div>
            </div>

            {/* Map Display */}
            {hasGeolocation && (
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary-600" />
                  Venue Location
                </h4>
                <VenueMap 
                  lat={venue.lat!} 
                  lon={venue.lon!} 
                  venueName={venue.property_name}
                  className="h-64"
                />
              </div>
            )}

            {!hasGeolocation && venue && (
              <Alert variant="warning">
                This venue hasn't been geocoded yet. Add an address and save to trigger automatic geocoding.
              </Alert>
            )}
          </TabsContent>

          {/* Venue Details Tab */}
          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Rooms
                </label>
                <Input
                  type="number"
                  value={formData.number_of_rooms}
                  onChange={(e) => handleChange('number_of_rooms', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Spaces
                </label>
                <Input
                  type="number"
                  value={formData.meeting_spaces}
                  onChange={(e) => handleChange('meeting_spaces', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Capacity
                </label>
                <Input
                  type="number"
                  value={formData.maximum_capacity}
                  onChange={(e) => handleChange('maximum_capacity', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Amenities Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Hotel className="w-5 h-5 text-primary-600" />
                Amenities
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Utensils className="w-4 h-4 inline mr-1" />
                    Restaurants
                  </label>
                  <Input
                    type="number"
                    value={formData.number_of_restaurants}
                    onChange={(e) => handleChange('number_of_restaurants', e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Wine className="w-4 h-4 inline mr-1" />
                    Bars/Lounges
                  </label>
                  <Input
                    type="number"
                    value={formData.number_of_bars}
                    onChange={(e) => handleChange('number_of_bars', e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Waves className="w-4 h-4 inline mr-1" />
                    Pools
                  </label>
                  <Input
                    type="number"
                    value={formData.number_of_pools}
                    onChange={(e) => handleChange('number_of_pools', e.target.value)}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Sparkles className="w-4 h-4 inline mr-1" />
                    Spa Facilities
                  </label>
                  <Select
                    value={formData.spa_facilities === null ? '' : formData.spa_facilities ? 'true' : 'false'}
                    onChange={(e) => handleChange('spa_facilities', e.target.value === '' ? null : e.target.value === 'true')}
                  >
                    <option value="">Unknown</option>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Palmtree className="w-4 h-4 inline mr-1" />
                    Beach Access
                  </label>
                  <Select
                    value={formData.beach_access}
                    onChange={(e) => handleChange('beach_access', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="beachfront">Beachfront</option>
                    <option value="beach_access">Beach Access</option>
                    <option value="none">None</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <UtensilsCrossed className="w-4 h-4 inline mr-1" />
                    All-Inclusive
                  </label>
                  <Select
                    value={formData.all_inclusive_available === null ? '' : formData.all_inclusive_available ? 'true' : 'false'}
                    onChange={(e) => handleChange('all_inclusive_available', e.target.value === '' ? null : e.target.value === 'true')}
                  >
                    <option value="">Unknown</option>
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Users className="w-4 h-4 inline mr-1" />
                    Property Type
                  </label>
                  <Select
                    value={formData.property_type}
                    onChange={(e) => handleChange('property_type', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="adults_only">Adults Only</option>
                    <option value="family_friendly">Family Friendly</option>
                    <option value="mixed">Mixed</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Flag className="w-4 h-4 inline mr-1" />
                    Golf Course
                  </label>
                  <Select
                    value={formData.golf_course}
                    onChange={(e) => handleChange('golf_course', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="onsite">On-site</option>
                    <option value="nearby">Nearby (within 5km)</option>
                    <option value="none">None</option>
                  </Select>
                </div>
              </div>
            </div>

            <Alert variant="info" className="mt-4">
              Use the <strong>AI Enrichment</strong> feature in the venues list to automatically populate these fields from web research.
            </Alert>
          </TabsContent>

          {/* Airports & Travel Tab */}
          <TabsContent value="airports" className="space-y-6">
            {hasAirportData ? (
              <>
                {/* Tab Header - Standardized pattern */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <Plane className="w-5 h-5 text-primary-600" />
                    Nearest Airport Information
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This information is automatically calculated based on the venue's geocoded location.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between py-3 border-b">
                    <span className="text-sm font-medium text-gray-700">Airport Code (IATA)</span>
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-gray-400" />
                      <span className="text-lg font-bold text-primary-600">{venue.nearest_airport_iata}</span>
                    </div>
                  </div>

                  {venue.nearest_airport_distance_km && (
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-sm font-medium text-gray-700">Distance</span>
                      <span className="text-sm text-gray-900">
                        {Number(venue.nearest_airport_distance_km).toFixed(1)} km
                      </span>
                    </div>
                  )}

                  {venue.travel_time_minutes && (
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-sm font-medium text-gray-700">Estimated Travel Time</span>
                      <span className="text-sm text-gray-900">
                        ~{venue.travel_time_minutes} minutes
                      </span>
                    </div>
                  )}
                </div>

                {/* Nearby Airports Section */}
                {venue.nearby_airports && venue.nearby_airports.length > 0 && (
                  <div className="pt-6 border-t">
                    <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Plane className="w-4 h-4 text-primary-600" />
                      Nearby Airports ({venue.nearby_airports.length})
                    </h4>
                    <div className="space-y-2">
                      {venue.nearby_airports.map((airport, idx) => (
                        <div
                          key={airport.iata}
                          className={`flex items-center justify-between py-2 px-3 rounded-lg border ${
                            idx === 0
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                              idx === 0
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-400 text-white'
                            }`}>
                              {airport.rank}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{airport.iata}</span>
                                {airport.name && airport.name !== airport.iata && (
                                  <span className="text-sm text-gray-500">{airport.name}</span>
                                )}
                                {airport.type && (
                                  <Badge 
                                    variant={airport.type === 'large_airport' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {airport.type === 'large_airport' ? 'Large' : airport.type === 'medium_airport' ? 'Medium' : airport.type.replace('_', ' ')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {airport.distance_km.toFixed(1)} km
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Alert variant="default" title="Note" className="mt-4">
                  <p className="text-xs">
                    Airport assignments are calculated automatically within a 100km radius when the venue is geocoded. Travel time is estimated based on average ground transportation speed (~60 km/h). Only large airports are shown.
                  </p>
                </Alert>
              </>
            ) : (
              <div className="text-center py-8">
                <Plane className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No airport data available</p>
                <p className="text-sm text-gray-500 mt-1">
                  Add an address and save to trigger automatic airport detection
                </p>
              </div>
            )}
          </TabsContent>

          {/* AI Agents Tab */}
          <TabsContent value="ai-agents" className="space-y-6">
            {/* Tab Header - Standardized pattern */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Brain className="w-5 h-5 text-primary-600" />
                AI Research Management
              </h3>
              <p className="text-sm text-muted-foreground">
                View research history and manage AI Research Agent activities for this venue.
              </p>
            </div>

            {/* Current Status Card */}
            <Card>
              <CardHeader noPadding>
                <CardTitle as="h4" className="text-base">Current Status</CardTitle>
                <CardDescription className="mt-1">
                  {venue?.enrichment_status === 'pending' ? 'Research in progress...' : 
                   venue?.enrichment_status === 'enriched' ? 'Last researched successfully' :
                   venue?.enrichment_status === 'failed' ? 'Last research failed' : 'Not yet researched'}
                </CardDescription>
              </CardHeader>

              {venue?.enrichment_completed_at && (
                <CardContent noPadding className="mt-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Last Completed:</span>{' '}
                    {new Date(venue.enrichment_completed_at).toLocaleString()}
                  </div>
                </CardContent>
              )}

              {/* Reset Research - Always visible with explanation */}
              <CardFooter noPadding className="mt-4 pt-4 border-t flex-col items-start gap-2">
                <p className="text-xs text-gray-500">
                  <strong>Troubleshooting:</strong> If research is stuck or shows incorrect status, you can reset it to retry.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => venue && resetMutation.mutate(venue.id)}
                  disabled={resetMutation.isPending}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset Research Status
                </Button>
              </CardFooter>
            </Card>

            {/* Research History - Accordion Style */}
            {enrichmentHistory.length > 0 ? (
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-gray-900">Research History</h5>
                <div className="space-y-3">
                  {enrichmentHistory.slice().reverse().map((entry, idx) => {
                    const isExpanded = expandedHistoryEntries.has(idx);
                    
                    return (
                      <Card key={idx} className="transition-all hover:shadow-md">
                        {/* Collapsed Summary Row - Always visible */}
                        <CardHeader 
                          className="cursor-pointer py-3" 
                          onClick={() => toggleHistoryEntry(idx)}
                          noPadding
                        >
                          <div className="flex items-center justify-between w-full">
                            {/* Left: Status icon and date */}
                            <div className="flex items-center gap-3">
                              {entry.status === 'enriched' ? (
                                <CheckCircle className="w-5 h-5 text-success-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-error-600" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {new Date(entry.completed_at).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric',
                                    hour: '2-digit', 
                                    minute: '2-digit'
                                  })}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {entry.status === 'enriched' ? 'Completed successfully' : 'Failed'} • {entry.duration_seconds}s
                                </p>
                              </div>
                            </div>
                            
                            {/* Right: Expand chevron */}
                            <ChevronDown 
                              className={`w-5 h-5 text-gray-400 transition-transform ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </CardHeader>

                        {/* Expandable Details */}
                        <CardExpandable isExpanded={isExpanded}>
                          <CardContent noPadding className="pt-4 border-t">
                            {/* Timestamps and Source */}
                            <div className="text-xs text-gray-600 space-y-1 mb-3">
                              <div>
                                <span className="font-medium">Started:</span>{' '}
                                {new Date(entry.started_at).toLocaleString()}
                              </div>
                              <div>
                                <span className="font-medium">Completed:</span>{' '}
                                {new Date(entry.completed_at).toLocaleString()}
                              </div>
                              <div>
                                <span className="font-medium">Source:</span> {entry.source}
                              </div>
                            </div>

                            {/* Fields Enriched */}
                            {entry.enriched_fields && entry.enriched_fields.length > 0 && (
                              <div className="mb-3">
                                <span className="text-xs font-medium text-gray-700 block mb-2">Fields Updated:</span>
                                <div className="flex flex-wrap gap-1">
                                  {entry.enriched_fields.map((field, fidx) => (
                                    <span 
                                      key={fidx}
                                      className="px-2 py-0.5 bg-success-100 text-success-800 rounded text-xs"
                                    >
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Agent Notes - Reduced spacing */}
                            {entry.agent_notes && entry.agent_notes.length > 0 && (
                              <div>
                                <span className="text-xs font-medium text-gray-700 block mb-2">AI Agent Notes:</span>
                                <ul className="space-y-0.5 list-none">
                                  {entry.agent_notes.map((note, nidx) => (
                                    <li 
                                      key={nidx}
                                      className="text-xs text-gray-700 py-1 leading-tight"
                                    >
                                      • {note}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Error */}
                            {entry.error && (
                              <div className="mt-3 p-2 bg-error-50 border border-error-200 rounded text-xs text-error-700">
                                <span className="font-medium">Error:</span> {entry.error}
                              </div>
                            )}
                          </CardContent>
                        </CardExpandable>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No research history yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  Run the AI research feature to see research history here
                </p>
              </div>
            )}

            {/* TripAdvisor Reviews Section */}
            {venue?.tripadvisor_reviews_json && Array.isArray(venue.tripadvisor_reviews_json) && venue.tripadvisor_reviews_json.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-primary-600" />
                  TripAdvisor Reviews (From AI Enrichment)
                </h5>
                <div className="space-y-3">
                  {venue.tripadvisor_reviews_json.map((review: any, idx: number) => (
                    <Card key={idx} className="bg-gray-50">
                      <CardContent noPadding className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h6 className="text-sm font-semibold text-gray-900">{review.title}</h6>
                            <p className="text-xs text-gray-500 mt-0.5">
                              by {review.author} • {new Date(review.date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            <StarRating rating={review.rating} size="sm" showEmptyStars={false} />
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{review.text}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {venue.tripadvisor_link && (
                  <div className="mt-3 text-center">
                    <a
                      href={venue.tripadvisor_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      View all reviews on TripAdvisor →
                    </a>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Notes Tab - Now with Comments System */}
          <TabsContent value="notes" className="space-y-4">
            {venue ? (
              <VenueComments
                venueId={venue.id}
                venueName={venue.property_name}
              />
            ) : (
              <Alert variant="info">
                Comments will be available after the venue is created. Save this venue first to start adding team comments.
              </Alert>
            )}
          </TabsContent>

          {/* Images Tab - Shared panel so photos are managed here too (not just the details modal) */}
          <TabsContent value="images" className="space-y-4">
            {venue ? (
              <VenueImagesPanel
                venueId={venue.id}
                placeId={formData.place_id}
                onPlaceIdChanged={(placeId) => setFormData((f: typeof formData) => ({ ...f, place_id: placeId }))}
              />
            ) : (
              <Alert variant="info">
                Images can be managed after the venue is created. Save this venue first, then upload photos or connect a Google Place.
              </Alert>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 pt-6 mt-6 border-t">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? 'Saving...' : venue ? 'Update Venue' : 'Create Venue'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}